import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LuShield } from "react-icons/lu";
import { PageHeader } from "@/components/ui/page-header";
import { ApiError, type ConsoleOperatorRow } from "@/lib/api";

export function OperatorsPage() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "viewer">("admin");

  const session = useQuery({
    queryKey: ["console", "auth", "me"],
    queryFn: () => api.auth.me(),
  });
  const operatorsQuery = useQuery({
    queryKey: ["console", "operators"],
    queryFn: () => api.operators.list(),
  });

  const inviteMutation = useMutation({
    mutationFn: api.operators.invite,
    onSuccess: () => {
      toast.success("Operator added");
      setEmail("");
      queryClient.invalidateQueries({ queryKey: ["console", "operators"] });
    },
    onError: (err: Error) => toast.error(err instanceof ApiError ? err.message : err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.operators.update>[1] }) =>
      api.operators.update(id, body),
    onSuccess: () => {
      toast.success("Operator updated");
      queryClient.invalidateQueries({ queryKey: ["console", "operators"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const myId = session.data?.consoleUserId;
  const columns: Column<ConsoleOperatorRow>[] = [
    {
      header: "Email",
      cell: (op) => (
        <span>
          {op.email}
          {op.id === myId && (
            <span className="ml-2 text-xs text-[var(--color-primary)]">(you)</span>
          )}
        </span>
      ),
    },
    { header: "Role", cell: (op) => op.role },
    {
      header: "Status",
      cell: (op) =>
        op.revoked_at ? (
          <span className="text-[var(--color-error)]">revoked</span>
        ) : op.expires_at && op.expires_at < new Date().toISOString() ? (
          <span className="text-[var(--color-warning)]">expired</span>
        ) : (
          <span className="text-[var(--color-success)]">active</span>
        ),
    },
    {
      header: "Invited",
      cell: (op) => new Date(op.created_at).toLocaleDateString(),
      className: "text-[var(--color-text-muted)]",
    },
    {
      header: "",
      cell: (op) => (
        <div className="flex gap-2 justify-end">
          {!op.revoked_at && op.id !== myId && (
            <Button
              variant="danger"
              size="sm"
              onClick={() =>
                updateMutation.mutate({ id: op.id, body: { revoked: true } })
              }
            >
              Revoke
            </Button>
          )}
          {op.revoked_at && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                updateMutation.mutate({ id: op.id, body: { revoked: false } })
              }
            >
              Reinstate
            </Button>
          )}
          <select
            value={op.role}
            onChange={(e) =>
              updateMutation.mutate({
                id: op.id,
                body: { role: e.target.value as "admin" | "viewer" },
              })
            }
            disabled={op.id === myId}
            className="px-2 py-1 text-xs rounded bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] disabled:opacity-50"
          >
            <option value="admin">admin</option>
            <option value="viewer">viewer</option>
          </select>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        icon={LuShield}
        title="Operators"
        description="Console operators authorised to access this dashboard. Cloudflare Access still gates the network layer; this list controls who can act on data."
      />
      <div className="p-8 space-y-6">
        <Card>
          <div className="px-5 py-4 border-b border-[var(--color-border)]">
            <p className="text-sm font-semibold text-[var(--color-text)]">Invite operator</p>
            <p className="text-xs text-[var(--color-text-muted)]">
              Their email must already pass the Cloudflare Access policy on
              admin.destroy.network.
            </p>
          </div>
          <div className="px-5 py-4 flex flex-wrap gap-3 items-end">
            <Input
              type="email"
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="max-w-sm"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as "admin" | "viewer")}
              className="px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)]"
            >
              <option value="admin">Admin</option>
              <option value="viewer">Viewer</option>
            </select>
            <Button
              onClick={() => inviteMutation.mutate({ email, role })}
              disabled={!email || inviteMutation.isPending}
            >
              Add operator
            </Button>
          </div>
        </Card>

        <Card>
          <DataTable
            columns={columns}
            rows={operatorsQuery.data?.operators}
            isLoading={operatorsQuery.isLoading}
            isError={operatorsQuery.isError}
            rowKey={(op) => op.id}
            emptyMessage="No operators yet."
          />
        </Card>
      </div>
    </>
  );
}
